import {inject, Injectable, InjectionToken, Injector, Type} from '@angular/core';
import {NgbModal, NgbModalConfig, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Observable, ReplaySubject, take} from 'rxjs';
import {DismissGuard, ModalCloser} from './modal-closer';

export type ModalWithTokens<TComponent, TInput, TResult> = Type<TComponent> & {
    readonly inputToken: InjectionToken<TInput>;
    readonly resultToken?: InjectionToken<ModalCloser<TResult>>;
};

export type ModalRefWithoutClose = Omit<NgbModalRef, 'close'>;

export type ModalHandle<TComponent, TResult> = {
    readonly modalRef: ModalRefWithoutClose;
    readonly instance: TComponent;
    /**
     * Emits once with the result when the modal closes; completes without emitting on dismiss.
     * Replays that outcome, so an opener that subscribes after the modal has already closed still
     * sees it.
     */
    readonly closed$: Observable<TResult>;
    /** Emits the dismissal reason, replayed the same way `closed$` is. */
    readonly dismissed$: Observable<unknown>;
    /** Closes from the opener side, against the same result type the modal itself closes with. */
    readonly close: (result: TResult) => void;
};

@Injectable({
    providedIn: 'root',
})
export class ModalInputService {
    private readonly modalService = inject(NgbModal);
    private readonly modalConfig = inject(NgbModalConfig);
    private readonly injector = inject(Injector);

    /**
     * `TResult` is inferred from the component's `resultToken` and falls back to `unknown` for a
     * modal that declares none, so a modal can adopt the result contract independently of its
     * input migration.
     */
    public open<TComponent, TInput, TResult = unknown>(
        component: ModalWithTokens<TComponent, TInput, TResult>,
        input: NoInfer<TInput>,
        options?: NgbModalOptions,
    ): ModalHandle<TComponent, TResult> {
        const closer = new ModalCloser<TResult>();
        // An explicit key wins over the `beforeDismiss` NgbModalConfig supplies (typed as always
        // present, in fact undefined unless an app sets one), so fold that guard in, never replace it.
        const modalRef = this.modalService.open(component, {
            ...options,
            beforeDismiss: this.composeBeforeDismiss(
                options?.beforeDismiss ?? this.modalConfig.beforeDismiss,
                () => closer.mayDismiss(),
            ),
            injector: Injector.create({
                providers: [
                    {provide: component.inputToken, useValue: input},
                    ...(component.resultToken == null
                        ? []
                        : [{provide: component.resultToken, useValue: closer}]),
                ],
                parent: options?.injector ?? this.injector,
            }),
        });
        closer.attach(modalRef);

        // The single sanctioned read: doing it once here is what gives every caller the real
        // component type instead of `any`.
        // eslint-disable-next-line no-restricted-syntax
        const created: unknown = modalRef.componentInstance;
        if (!(created instanceof component)) {
            throw new Error(`ngbModal did not create a ${component.name}`);
        }

        // `NgbModalRef.closed` is `Observable<any>`, and narrowing it once here is what spares
        // every call site an assertion. The handle withholds `NgbModalRef.close`, so the opener
        // can only close through `ModalCloser.close`, which accepts nothing but `TResult`. The
        // modal itself has to hold to that too: ng-bootstrap provides `NgbActiveModal` to every
        // modal, and closing through it puts an unchecked value on this stream.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const liveClosed$: Observable<TResult> = modalRef.closed;

        return {
            modalRef,
            instance: created,
            closed$: this.replayOutcome(liveClosed$),
            dismissed$: this.replayOutcome<unknown>(modalRef.dismissed),
            close: (result: TResult): void => {
                closer.close(result);
            },
        };
    }

    /**
     * `NgbModalRef.closed` and `dismissed` are plain Subjects behind `takeUntil(hidden)`, so once
     * the hide transition has finished a new subscriber neither emits nor completes. Mirroring
     * them while the modal is still open is what lets an opener subscribe late. `take(1)` because
     * ng-bootstrap accepts a second `close()` or `dismiss()` while the hide animation runs.
     */
    private replayOutcome<T>(source: Observable<T>): Observable<T> {
        const replayed = new ReplaySubject<T>(1);
        source.pipe(take(1)).subscribe(replayed);
        return replayed.asObservable();
    }

    /**
     * Stays synchronous while both hooks are, so a modal that registers no guard dismisses in the
     * same tick it always did.
     */
    private composeBeforeDismiss(
        callerGuard: DismissGuard | undefined,
        modalGuard: DismissGuard,
    ): DismissGuard {
        return () => {
            const callerVerdict = callerGuard?.() ?? true;
            if (callerVerdict === false) {
                return false;
            }
            if (callerVerdict === true) {
                return modalGuard();
            }
            return callerVerdict.then(mayDismiss => (mayDismiss ? modalGuard() : false));
        };
    }
}
